var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { preferencesService } from "../../modules/preferences/PreferencesService";
import { MessageService } from "../services/message.service";
import { localStreamingService } from "../../modules/multiplayer/peer/LocalStreamingService";
import { remoteStreamingService } from "../../modules/multiplayer/peer/RemoteStreamingService";
import i18next from "i18next";
import { busService } from "../../modules/bus/BusService";
import { AudioOutputChangedMessage } from "../../messages/media/LocalMediaMessages";
const DEFAULT_DEVICE_ID = "default";
function createInitialDeviceState(enabled, setDeviceIdFn, setEnabledFn, storeAccess) {
    return {
        deviceId: undefined,
        setDeviceId: (deviceId, persist = false) => {
            setDeviceIdFn(deviceId, persist, storeAccess.set, storeAccess);
        },
        enabled: enabled,
        setEnabled: (enabled, persist = false) => {
            setEnabledFn(enabled, persist, storeAccess);
        }
    };
}
export const useMediaDevicesStore = create((set, get) => ({
    mic: createInitialDeviceState(false, setMicDeviceId, setMicEnabled, { set: set, get: get }),
    cam: createInitialDeviceState(false, setCamDeviceId, setCamEnabled, { set: set, get: get }),
    speaker: createInitialDeviceState(true, setSpeakerDeviceId, setSpeakerEnabled, { set: set, get: get })
}));
function persistMediaDevicesState() {
    var _a, _b, _c;
    const store = useMediaDevicesStore.getState();
    preferencesService.updatePreferences([
        { key: "microphoneDeviceId", value: (_a = store.mic.deviceId) !== null && _a !== void 0 ? _a : DEFAULT_DEVICE_ID },
        { key: "microphoneDefaultEnabled", value: store.mic.enabled },
        { key: "cameraDeviceId", value: (_b = store.cam.deviceId) !== null && _b !== void 0 ? _b : DEFAULT_DEVICE_ID },
        { key: "cameraDefaultEnabled", value: store.cam.enabled },
        { key: "speakersDeviceId", value: (_c = store.speaker.deviceId) !== null && _c !== void 0 ? _c : DEFAULT_DEVICE_ID },
        { key: "speakersDefaultEnabled", value: store.speaker.enabled }
    ], true);
}
function loadPersistedState() {
    const store = useMediaDevicesStore.getState();
    store.mic.setDeviceId(preferencesService.getPreferredValue("microphoneDeviceId"));
    store.mic.setEnabled(preferencesService.getPreferredValue("microphoneDefaultEnabled"));
    store.cam.setDeviceId(preferencesService.getPreferredValue("cameraDeviceId"));
    store.cam.setEnabled(preferencesService.getPreferredValue("cameraDefaultEnabled"));
    store.speaker.setDeviceId(preferencesService.getPreferredValue("speakersDeviceId"));
    store.speaker.setEnabled(preferencesService.getPreferredValue("speakersDefaultEnabled"));
}
loadPersistedState();
function setStreamingDeviceEnabled(deviceType, enabled, persist, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (enabled) {
            // Check availability and show message if not available
            try {
                const constraints = deviceType === "mic"
                    ? { audio: { deviceId: storeAccess.get()[deviceType].deviceId }, video: false }
                    : { video: { deviceId: storeAccess.get()[deviceType].deviceId, width: 320 }, audio: false };
                const stream = yield ((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia(constraints));
                if (stream === undefined) {
                    throw Error('MediaDevice is not available inside a Worker Thread');
                }
                stream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            catch (error) {
                console.error("[ui]", error);
                enabled = false;
                MessageService.showMessage("warn", i18next.t("common:warning-device-in-use", "The selected device is already in use"));
            }
        }
        if (enabled && storeAccess.get()[deviceType].deviceId) {
            // TODO: Some kind of error handling for missing deviceId would be nice
            const startStreaming = () => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (!window.location.search.includes("session")) {
                    // No session means we are on the landing page - no need to actually stream, just set the state
                    storeAccess.set((state) => ({ [deviceType]: Object.assign(Object.assign({}, state[deviceType]), { enabled: enabled }) }));
                    return;
                }
                if (!localStreamingService.isReady()) {
                    setTimeout(startStreaming, 250);
                    return;
                }
                const startFunction = deviceType === "mic" ? localStreamingService.startMicrophoneStreaming.bind(localStreamingService) : localStreamingService.startCameraStreaming.bind(localStreamingService);
                startFunction((_a = storeAccess.get()[deviceType].deviceId) !== null && _a !== void 0 ? _a : "")
                    .then(() => {
                    storeAccess.set((state) => ({ [deviceType]: Object.assign(Object.assign({}, state[deviceType]), { enabled: true }) }));
                })
                    .catch((error) => {
                    console.error("[ui] Error in startStreaming", error);
                    void setStreamingDeviceEnabled(deviceType, false, persist, storeAccess);
                });
            });
            yield startStreaming();
        }
        else {
            storeAccess.set((state) => ({ [deviceType]: Object.assign(Object.assign({}, state[deviceType]), { enabled: false }) }));
            if (localStreamingService.isReady()) {
                const stopFunction = deviceType === "mic" ? localStreamingService.stopMicrophoneStreaming.bind(localStreamingService) : localStreamingService.stopCameraStreaming.bind(localStreamingService);
                stopFunction();
            }
        }
        if (persist) {
            persistMediaDevicesState();
        }
    });
}
function setMicEnabled(micEnabled, persist, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        yield setStreamingDeviceEnabled("mic", micEnabled, persist, storeAccess);
    });
}
function setCamEnabled(camEnabled, persist, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        yield setStreamingDeviceEnabled("cam", camEnabled, persist, storeAccess);
    });
}
function setStreamingDeviceId(deviceType, deviceId, persist, set, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        let enableAfterChange = false;
        // Disabling the device and enabling it again after setting the deviceId makes sure, that the availablitity of the device gets checked
        if (storeAccess.get()[deviceType].enabled) {
            yield setStreamingDeviceEnabled(deviceType, false, false, storeAccess);
            enableAfterChange = true;
        }
        set((state) => ({ [deviceType]: Object.assign(Object.assign({}, state[deviceType]), { deviceId: deviceId }) }));
        if (enableAfterChange) {
            yield setStreamingDeviceEnabled(deviceType, true, false, storeAccess);
        }
        if (persist) {
            // TODO: Shouldn't deviceId always be persisted???
            persistMediaDevicesState();
        }
    });
}
function setMicDeviceId(micDeviceId, persist, set, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        yield setStreamingDeviceId("mic", micDeviceId, persist, set, storeAccess);
    });
}
function setCamDeviceId(camDeviceId, persist, set, storeAccess) {
    return __awaiter(this, void 0, void 0, function* () {
        yield setStreamingDeviceId("cam", camDeviceId, persist, set, storeAccess);
    });
}
function setSpeakerEnabled(speakerEnabled, persist, storeAccess) {
    remoteStreamingService.setGloballyMuted(!speakerEnabled);
    storeAccess.set((state) => ({ speaker: Object.assign(Object.assign({}, state.speaker), { enabled: speakerEnabled }) }));
    if (persist) {
        persistMediaDevicesState();
    }
}
function setSpeakerDeviceId(speakerDeviceId, persist, set) {
    return __awaiter(this, void 0, void 0, function* () {
        set((state) => ({ speaker: Object.assign(Object.assign({}, state.speaker), { deviceId: speakerDeviceId }) }));
        if (persist) {
            persistMediaDevicesState();
        }
        const sendUpdate = () => {
            if (busService.connection("MediaDevicesStore").isReady()) {
                busService.connection("MediaDevicesStore").send(new AudioOutputChangedMessage());
            }
            else {
                setTimeout(sendUpdate, 100);
            }
        };
        sendUpdate();
    });
}
